import { CandidateCategory, CandidateStatus } from "../api";
import { CandidateFull } from "./candidate";
import {
  RECOMMENDATION_BUCKET_SCORES,
  toRecommendationBucket,
} from "./evaluation";

export enum Status {
  all = "All",
  future = "Future",
  past = "Past",
  archived = "Archived",
  withdrawn = "Withdrawn",
}

export type CandidateFilter = {
  status: keyof typeof Status;
  evaluationItem: {
    titles: string[];
  };
};

export type CandidateSorter = {
  key:
    | "candidate"
    | "stage"
    | "evaluation"
    | "callTime"
    | "status"
    | "lastMessage"
    | "none";
  direction: "asc" | "desc";
};

const candidateComparators: Record<
  CandidateSorter["key"],
  (a: CandidateFull, b: CandidateFull) => number
> = {
  candidate: (a, b) => {
    const aCandidate = a.display_name || "";
    const bCandidate = b.display_name || "";
    return aCandidate < bCandidate ? -1 : 1;
  },
  stage: (a, b) => {
    const aStage = a.stage || "";
    const bStage = b.stage || "";

    if (aStage === bStage) return 0;
    return aStage < bStage ? -1 : 1;
  },
  evaluation: (a, b) => {
    const aScore =
      RECOMMENDATION_BUCKET_SCORES[
        toRecommendationBucket(a.evaluation?.recommendation)
      ];
    const bScore =
      RECOMMENDATION_BUCKET_SCORES[
        toRecommendationBucket(b.evaluation?.recommendation)
      ];

    if (aScore === bScore) {
      // Same tier (or both missing) — fall back to rank, lower-is-better, but
      // with the existing reverse ordering preserved for compatibility.
      if (a.rank === null && b.rank === null) return 0;
      if (a.rank === null) return -1;
      if (b.rank === null) return 1;
      return b.rank - a.rank;
    }
    return aScore < bScore ? -1 : 1;
  },
  callTime: (a, b) => {
    if (a.local_call_time === b.local_call_time) return 0;
    if (a.local_call_time === null) return 1;
    if (b.local_call_time === null) return -1;
    return a.local_call_time < b.local_call_time ? -1 : 1;
  },
  status: (a, b) => {
    const scores: Record<CandidateStatus, number> = {
      [CandidateStatus.NotDelivered]: 0,
      [CandidateStatus.Scheduling]: 1,
      [CandidateStatus.Scheduled]: 2,
      [CandidateStatus.Ongoing]: 3,
      [CandidateStatus.Rescheduling]: 4,
      [CandidateStatus.Completed]: 5,
      [CandidateStatus.NeedToContact]: 6,
      [CandidateStatus.Withdrawn]: 7,
    };

    if (a.status === b.status) return 0;
    if (a.status === null) return 1;
    if (b.status === null) return -1;
    return scores[a.status] < scores[b.status] ? -1 : 1;
  },
  lastMessage: (a, b) => {
    if (a.local_last_message_time === b.local_last_message_time) return 0;
    if (a.local_last_message_time === null) return 1;
    if (b.local_last_message_time === null) return -1;
    return a.local_last_message_time < b.local_last_message_time ? -1 : 1;
  },
  none: (_a, _b) => 0,
};

export class Candidates extends Array<CandidateFull> {
  constructor(candidates?: CandidateFull[]) {
    super();
    if (candidates instanceof Array)
      candidates.forEach((candidate) => this.push(candidate));
  }

  public get(candidateId: number): CandidateFull | undefined {
    return this.find((candidate) => candidate.id === candidateId);
  }

  get latest(): CandidateFull | undefined {
    return this.length > 0 ? this[0] : undefined;
  }

  public update(newCandidate: CandidateFull): Candidates {
    if (this.get(newCandidate.id) === undefined)
      return new Candidates([...this, newCandidate]);
    return new Candidates(
      this.map((candidate) =>
        candidate.id === newCandidate.id ? newCandidate : candidate,
      ),
    );
  }

  public append(newCandidates: Candidates): Candidates {
    return new Candidates([
      ...this.filter(
        (candidate) => newCandidates.get(candidate.id) === undefined,
      ),
      ...newCandidates,
    ]);
  }

  public remove(candidate: CandidateFull): Candidates {
    return new Candidates(this.filter((c) => c.id !== candidate.id));
  }

  public sortBy(sorter: CandidateSorter): Candidates {
    const sortMultiplier = sorter.direction === "asc" ? 1 : -1;

    return new Candidates(
      this.slice().sort((a, b) => {
        if (sorter.key === "callTime") {
          if (a.local_call_time === null)
            return b.local_call_time === null ? 0 : 1;
          if (b.local_call_time === null) return -1;
        }
        return sortMultiplier * candidateComparators[sorter.key](a, b);
      }),
    );
  }

  public filterByStatus(filter: keyof typeof Status): Candidates {
    return new Candidates(
      this.filter((candidate) => {
        // Filter by status
        if (Status[filter] === Status.all)
          return (
            candidate.archived === false &&
            candidate.status !== CandidateStatus.Withdrawn
          );
        if (Status[filter] === Status.future) return candidate.isFuture();
        if (Status[filter] === Status.past) return candidate.isPast();
        if (Status[filter] === Status.archived)
          return (
            candidate.archived === true &&
            candidate.status !== CandidateStatus.Withdrawn
          );
        if (Status[filter] === Status.withdrawn)
          return (
            candidate.status === CandidateStatus.Withdrawn &&
            candidate.archived === false
          );
        return false;
      }),
    );
  }

  public filterByCandidateStatus(
    filter: CandidateStatus,
    operation: "equalTo" | "notEqualTo" = "equalTo",
  ): Candidates {
    if (operation === "equalTo") {
      return new Candidates(
        this.filter((candidate) => candidate.status === filter),
      );
    } else {
      return new Candidates(
        this.filter((candidate) => candidate.status !== filter),
      );
    }
  }

  public filterByCandidateStatuses(
    filters: CandidateStatus[],
    operation: "equalTo" | "notEqualTo" = "equalTo",
  ): Candidates {
    if (operation === "equalTo") {
      return new Candidates(
        this.filter((candidate) => {
          if (!candidate.status) return false;
          return filters.includes(candidate.status);
        }),
      );
    } else {
      return new Candidates(
        this.filter((candidate) => {
          if (!candidate.status) return false;
          return !filters.includes(candidate.status);
        }),
      );
    }
  }

  public filterByCategory(filter: CandidateCategory): Candidates {
    return new Candidates(
      this.filter((candidate) => {
        return candidate.category === filter;
      }),
    );
  }

  public filterByCategories(
    filters: CandidateCategory[],
    operation: "equalTo" | "notEqualTo" = "equalTo",
  ): Candidates {
    if (operation === "equalTo") {
      return new Candidates(
        this.filter((candidate) => {
          if (!candidate.category) return false;
          return filters.includes(candidate.category);
        }),
      );
    } else {
      return new Candidates(
        this.filter((candidate) => {
          if (!candidate.category) return false;
          return !filters.includes(candidate.category);
        }),
      );
    }
  }

  public filterByCategoriesOrCandidateStatuses(
    categories: CandidateCategory[],
    candidateStatuses: CandidateStatus[],
    categoryOperation: "equalTo" | "notEqualTo" = "equalTo",
    candidateStatusOperation: "equalTo" | "notEqualTo" = "equalTo",
  ): Candidates {
    return new Candidates(
      this.filter((candidate) => {
        let categoryBool = false;
        if (!candidate.category) {
          categoryBool = false;
        } else {
          if (categoryOperation === "equalTo") {
            categoryBool = categories.includes(candidate.category);
          } else {
            categoryBool = !categories.includes(candidate.category);
          }
        }

        let candidateStatusBool = false;
        if (!candidate.status) {
          candidateStatusBool = false;
        } else {
          if (candidateStatusOperation === "equalTo") {
            candidateStatusBool = candidateStatuses.includes(candidate.status);
          } else {
            candidateStatusBool = !candidateStatuses.includes(candidate.status);
          }
        }

        return categoryBool || candidateStatusBool;
      }),
    );
  }

  public queryBy(query: string): Candidates {
    return new Candidates(
      this.filter((candidate) => {
        return candidate.query(query);
      }),
    );
  }

  public notWithdrawn(): Candidates {
    return this.filterByCandidateStatus(
      CandidateStatus.Withdrawn,
      "notEqualTo",
    );
  }

  public nonArchivedCandidates(): Candidates {
    return this.filterByCandidateStatus(
      CandidateStatus.Withdrawn,
      "notEqualTo",
    ).filterByStatus("all");
  }

  public archivedCandidates(): Candidates {
    return this.filterByStatus("archived");
  }

  // for steplist count
  public getAISchedulingCandidates(): Candidates {
    return this.filterByCategories([
      CandidateCategory.NeedFollowUp,
      CandidateCategory.InProcess,
    ]);
  }

  public getAISchedulingCount(): number {
    return this.getAISchedulingCandidates().length;
  }

  public getAISchedulingCountWithStatus(
    status: CandidateStatus,
    operation: "equalTo" | "notEqualTo" = "equalTo",
  ): Candidates {
    return this.getAISchedulingCandidates().filterByCandidateStatus(
      status,
      operation,
    );
  }

  public getPhoneScreeningCountWithStatus(
    status: CandidateStatus,
    operation: "equalTo" | "notEqualTo" = "equalTo",
  ): Candidates {
    return this.getPhoneScreeningCandidates().filterByCandidateStatus(
      status,
      operation,
    );
  }

  // for steplist count
  public getPhoneScreeningCandidates(): Candidates {
    return this.filterByCategories([
      CandidateCategory.RequestForApproval,
      CandidateCategory.Unqualified,
    ]);
  }

  public getPhoneScreeningCount(): number {
    return this.getPhoneScreeningCandidates().length;
  }

  // for scheduling list table
  public getNeedFollowUpCandidates(): Candidates {
    return this.filterByCategory(CandidateCategory.NeedFollowUp).notWithdrawn();
  }

  // for scheduling list table
  public getInProcessCandidates(): Candidates {
    return this.filterByCategory(CandidateCategory.InProcess)
      .filterByCandidateStatus(CandidateStatus.Scheduled, "notEqualTo")
      .notWithdrawn();
  }

  // for scheduling list table
  public getScheduledCandidates(): Candidates {
    return this.filterByCandidateStatus(
      CandidateStatus.Scheduled,
    ).notWithdrawn();
  }

  // for scheduling list table
  public getAISchedulingWithdrawnCandidates(): Candidates {
    return new Candidates(
      this.getAISchedulingCandidates().filter(
        (candidate) => candidate.status === CandidateStatus.Withdrawn,
      ),
    );
  }

  public getAISchedulingArchivedWithdrawnCandidates(): Candidates {
    return new Candidates(
      this.getAISchedulingCandidates().filter(
        (candidate) =>
          candidate.status === CandidateStatus.Withdrawn &&
          candidate.archived === true,
      ),
    );
  }

  public getAISchedulingNonArchivedWithdrawnCandidates(): Candidates {
    return new Candidates(
      this.getAISchedulingCandidates().filter(
        (candidate) =>
          candidate.status === CandidateStatus.Withdrawn &&
          candidate.archived === false,
      ),
    );
  }
}
